import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

export type ViewCartStylesParams = {
  viewCartDefaultFont: StyleParamType.Font;
  viewCartDefaultTextColor: StyleParamType.Color;
  viewCartDefaultBackgroundColor: StyleParamType.Color;
  viewCartDefaultBorderWidth: StyleParamType.Number;
  viewCartDefaultBorderColor: StyleParamType.Color;
  viewCartCornerRadiusTopLeft: StyleParamType.Number;
  viewCartCornerRadiusTopRight: StyleParamType.Number;
  viewCartCornerRadiusBottomLeft: StyleParamType.Number;
  viewCartCornerRadiusBottomRight: StyleParamType.Number;
  viewCartCornerRadiusLinked: StyleParamType.Number;

  viewCartHoverFont: StyleParamType.Font;
  viewCartHoverTextColor: StyleParamType.Color;
  viewCartHoverBackgroundColor: StyleParamType.Color;
  viewCartHoverBorderColor: StyleParamType.Color;

  viewCartShowIcon: StyleParamType.Boolean;
  viewCartIconType: StyleParamType.Number;
  viewCartIconSize: StyleParamType.Number;
  viewCartIconColor: StyleParamType.Color;
};

export const viewCartStylesParams = createStylesParams<ViewCartStylesParams>({
  viewCartDefaultFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) =>
      wixFontParam('Body-M', {
        htmlTag: 'p',
        size: params.isMobile ? 14 : 16,
      })(params),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: {
        description: 'Font of the Add to Cart / view cart button label',
      },
    },
  },
  viewCartDefaultTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
      ai: {
        description: 'Text color of the Add to Cart / view cart button label',
      },
    },
  },
  viewCartDefaultBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: {
        description: 'Background color of the Add to Cart / view cart button',
      },
    },
  },
  viewCartDefaultBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Border thickness in pixels of the Add to Cart / view cart button',
      },
    },
  },
  viewCartDefaultBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.12),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.12 },
      ai: {
        description: 'Border color of the Add to Cart / view cart button',
      },
    },
  },
  viewCartCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-left corner radius in pixels of the Add to Cart / view cart button',
      },
    },
  },
  viewCartCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-right corner radius in pixels of the Add to Cart / view cart button',
      },
    },
  },
  viewCartCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-left corner radius in pixels of the Add to Cart / view cart button',
      },
    },
  },
  viewCartCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-right corner radius in pixels of the Add to Cart / view cart button',
      },
    },
  },
  viewCartCornerRadiusLinked: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 1 },
      ai: {
        description:
          'Controls whether the Add to Cart / view cart button corner radii are linked — 1 = all corners locked together, 0 = each corner independent',
      },
    },
  },

  viewCartHoverFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) =>
      wixFontParam('Body-M', {
        htmlTag: 'p',
        size: params.isMobile ? 14 : 16,
      })(params),
    metadata: {
      type: StyleType.font,
      defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
      mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
      ai: {
        description:
          'Font of the Add to Cart / view cart button label on hover',
      },
    },
  },
  viewCartHoverTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
      ai: {
        description:
          'Text color of the Add to Cart / view cart button label on hover',
      },
    },
  },
  viewCartHoverBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
      ai: {
        description:
          'Background color of the Add to Cart / view cart button on hover',
      },
    },
  },
  viewCartHoverBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.12),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.12 },
      ai: {
        description:
          'Border color of the Add to Cart / view cart button on hover',
      },
    },
  },

  viewCartShowIcon: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: { type: 'static', value: false },
      ai: {
        description:
          'Controls whether the icon is shown on the Add to Cart / view cart button',
      },
    },
  },
  viewCartIconType: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Controls the icon shown in the view cart button — 0 = Cart, 1 = EcomCart, 2 = Stores, 3 = ShoppingBag',
      },
    },
  },
  viewCartIconSize: {
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 16 : 24),
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: 24 },
      mobileDefaultValue: { type: 'static', value: 16 },
      ai: {
        description:
          'Size in pixels of the icon on the Add to Cart / view cart button',
      },
    },
  },
  viewCartIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
      ai: {
        description: 'Color of the icon on the Add to Cart / view cart button',
      },
    },
  },
});
